import { useEffect, useState } from 'react'
import {
  CheckIcon,
  LabRequisition,
  ModalDialog,
  Mtds,
  SampleCompromised,
  SubSamples,
  SaveError,
  useSampleCompromisedMutation,
  XIcon
} from 'prove-common'

type DismissSampleModalProps = {
  isOpen: boolean
  setOpen: (isOpen: boolean) => void
  requisition: LabRequisition
}

export const DismissSampleModal = ({
  isOpen,
  setOpen,
  requisition
}: DismissSampleModalProps) => {
  const [inputReason, setInputReason] = useState<string>('')
  const requisitionId = requisition.id
  const sampleId = requisition.sampleId
  const subSamples: SubSamples = requisition.subSamples
  const sampleCompromisedMutation = useSampleCompromisedMutation()
  const [error, setError] = useState<unknown>(null)
  const onSubmit = async (): Promise<void> => {
    setError(null)
    const sampleCompromised: SampleCompromised = {
      subSamples: subSamples.map((subSample) => ({
        subsampleid: subSample.subSampleId,
        state: true,
        reason: inputReason
      }))
    }
    try {
      await sampleCompromisedMutation.mutateAsync({
        requisitionId,
        body: sampleCompromised
      })
      Mtds.toast.success('Sample is dismissed')
      setOpen(false)
    } catch (error) {
      setError(error)
    }
  }

  useEffect(() => {
    if (!isOpen) {
      setInputReason('')
      setError(null)
    }
  }, [isOpen])

  return (
    <ModalDialog
      id='dismiss-sample-modal'
      closedby='any'
      open={isOpen}
      onClose={() => setOpen(false)}
    >
      <Mtds.Button
        command='close'
        commandfor={'dismiss-sample-modal'}
        aria-label={'Close'}
      />
      <Mtds.Prose
        as='form'
        onSubmit={(event) => {
          event.preventDefault()
          void onSubmit()
        }}
      >
        <Mtds.Heading>Give reason for dismissal</Mtds.Heading>
        <p>
          To dismiss this sample (<strong># {sampleId}</strong>) you need to
          explain why this sample is not fit for analyzing.
        </p>
        <Mtds.Field
          as={Mtds.Textarea}
          label={'Reason for dismissal'}
          value={inputReason}
          onChange={({ target }) => setInputReason(target.value)}
          required={true}
        />
        <SaveError error={error} action='dismiss the sample'>
          Your reason has been kept. You can retry without entering it again.
        </SaveError>
        <Mtds.Flex>
          <Mtds.Button command='close' commandfor={'dismiss-sample-modal'}>
            <XIcon />
            Cancel
          </Mtds.Button>
          <Mtds.Button
            data-variant='primary'
            type='submit'
            aria-busy={sampleCompromisedMutation.isPending}
            disabled={sampleCompromisedMutation.isPending}
          >
            <CheckIcon />
            Dismiss sample with reason
          </Mtds.Button>
        </Mtds.Flex>
      </Mtds.Prose>
    </ModalDialog>
  )
}
